'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from 'next/navigation';
import { createContext, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import { useIsClient } from 'usehooks-ts';
import { SEARCH_PARAM_KEYS } from '@/constants/auth';
import { resolveControlBarPresentation, type ControlBarPresentation } from '@/constants/control-bar';
import { IFRAME_SEARCH_PARAM_KEYS } from '@/constants/iframe';
import { CONTROL_BAR_RECOMMENDATIONS_LIMIT, LOGO_OVERLAY_RECOMMENDATIONS_LIMIT, RECOMMEND_VISITOR_ID_COOKIE_KEY } from '@/constants/recommend';
import { useBISdkContext } from '@/context/BISdkContext';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useUAContext } from '@/context/UAContext';
import { useEnableFull3d } from '@/hooks/useEnableFull3d';
import { useFullscreenStatus } from '@/hooks/useFullscreenStatus';
import { useIsEmbedded } from '@/hooks/useIsEmbedded';
import { getClientCookie } from '@/lib/client-cookie';
import type { LogoOverlayContentModel } from '@/lib/logo-overlay';
import type { RecommendRoom } from '@/services/cms';
import { fetchLogoOverlayRecommendations } from '@/services/world-recommendations';
import type { IframePolicy } from '@/types/scene';
type WorldDetailsContextType = {
  controlBarPresentation: ControlBarPresentation;
  standaloneUrl: string;
  iframeSettings: IframePolicy[];
  isFullscreen: boolean;
  isEmbeddedRequest: boolean;
  /**
   * True for an embedded page whose content would get the embedded Control Bar if the URL had
   * `control_bar`, but whose URL does not. Content that never gets the Control Bar (Premium, video,
   * trailer, unsupported device) is false.
   */
  isEmbeddedWithoutControlBar: boolean;
  is3DCommerce: boolean;
  isVideoWorld: boolean;
  showMobilePlayButton: boolean;
  logoOverlayModel: LogoOverlayContentModel | null;
  logoOverlayRoomsPromise: Promise<RecommendRoom[]> | null;
  requestControlBarMoreRecommendations: () => void;
  requestLogoOverlayRecommendations: () => void;
  exitRecommendationsPromise: Promise<RecommendRoom[]>;
};
const WorldDetailsContext = createContext<WorldDetailsContextType | null>(null);
type WorldDetailsProviderProps = PropsWithChildren<{
  logoOverlayModel: LogoOverlayContentModel | null;
  exitRecommendationsPromise: Promise<RecommendRoom[]>;
  isEmbeddedRequest: boolean;
}>;
export function WorldDetailsProvider(t0) {
  const $ = _c(51);
  const {
    children,
    logoOverlayModel,
    exitRecommendationsPromise,
    isEmbeddedRequest
  } = t0;
  const {
    roomData,
    isOwnerViversePro,
    isPreferredDevice,
    isPremiumRoom,
    isVoucherCheckPassed
  } = useCheckEnterRoomContext();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get(SEARCH_PARAM_KEYS.PREVIEW);
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const previewMode = t1 !== null;
  const sceneType = roomData.scene_type;
  const sceneMeta = roomData.scene_type === "pc_scene_group" ? roomData.scene.meta : roomData.scene_type === "spoke_scene" ? undefined : roomData.scene.scene_meta;
  const sceneSettings = roomData.scene.settings;
  const standaloneUrl = sceneType === "standalone" || sceneType === "blendvision" ? previewMode ? sceneMeta?.preview_url ?? "" : sceneMeta && "scene_url" in sceneMeta ? sceneMeta.scene_url : "" : "";
  let t2;
  if ($[2] !== previewMode || $[3] !== roomData.scene.iframe_settings?.preview || $[4] !== roomData.scene.iframe_settings?.publish || $[5] !== roomData.scene_type) {
    t2 = roomData.scene_type === "standalone" || roomData.scene_type === "blendvision" ? previewMode ? roomData.scene.iframe_settings?.preview ?? [] : roomData.scene.iframe_settings?.publish ?? [] : [];
    $[2] = previewMode;
    $[3] = roomData.scene.iframe_settings?.preview;
    $[4] = roomData.scene.iframe_settings?.publish;
    $[5] = roomData.scene_type;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const iframeSettings = t2;
  let t3;
  if ($[7] !== sceneSettings?.eCommerceConfigs?.is3DEC || $[8] !== sceneSettings?.experimentalFlags) {
    t3 = sceneSettings?.experimentalFlags?.includes("3DEC_short") || sceneSettings?.eCommerceConfigs?.is3DEC;
    $[7] = sceneSettings?.eCommerceConfigs?.is3DEC;
    $[8] = sceneSettings?.experimentalFlags;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const is3DCommerce = Boolean(t3);
  const {
    isMobile,
    isMobileApp
  } = useUAContext();
  const isVideoWorld = sceneType === "blendvision" || !!roomData?.vod_id;
  const isEmbedded = useIsEmbedded(isEmbeddedRequest);
  const hasCheckedEmbeddedFrame = useIsClient();
  const isFull3D = useEnableFull3d();
  const {
    isFullscreen
  } = useFullscreenStatus();
  let controlBarPresentation;
  let t4;
  if ($[10] !== hasCheckedEmbeddedFrame || $[11] !== isEmbedded || $[12] !== isFull3D || $[13] !== isFullscreen || $[14] !== isMobile || $[15] !== isMobileApp || $[16] !== isOwnerViversePro || $[17] !== isPreferredDevice || $[18] !== isPremiumRoom || $[19] !== isVideoWorld || $[20] !== sceneType || $[21] !== searchParams) {
    const hasControlBarQueryParam = searchParams.has(IFRAME_SEARCH_PARAM_KEYS.CONTROL_BAR);
    const controlBarPresentationOptions = {
      hasCheckedEmbeddedFrame,
      hasControlBarQueryParam,
      hasKidsModeQueryParam: searchParams.has(IFRAME_SEARCH_PARAM_KEYS.KIDS_MODE),
      isEmbedded,
      isFull3D,
      isFullscreen,
      isMobileApp,
      isMobile,
      isPreferredDevice,
      isPremiumContent: isPremiumRoom,
      isStandaloneContent: sceneType === "standalone",
      isVideoContent: isVideoWorld,
      isViverseProOwner: isOwnerViversePro
    };
    controlBarPresentation = resolveControlBarPresentation(controlBarPresentationOptions);
    t4 = isEmbedded && !hasControlBarQueryParam && resolveControlBarPresentation({
      ...controlBarPresentationOptions,
      hasControlBarQueryParam: true
    }).kind === "embedded";
    $[10] = hasCheckedEmbeddedFrame;
    $[11] = isEmbedded;
    $[12] = isFull3D;
    $[13] = isFullscreen;
    $[14] = isMobile;
    $[15] = isMobileApp;
    $[16] = isOwnerViversePro;
    $[17] = isPreferredDevice;
    $[18] = isPremiumRoom;
    $[19] = isVideoWorld;
    $[20] = sceneType;
    $[21] = searchParams;
    $[22] = controlBarPresentation;
    $[23] = t4;
  } else {
    controlBarPresentation = $[22];
    t4 = $[23];
  }
  const isEmbeddedWithoutControlBar = t4;
  const worldId = roomData?.hub_sid;
  const {
    isLoadedBISdk,
    isBISdkFailed
  } = useBISdkContext();
  const {
    appConfig
  } = useConfigsContext();
  const shouldWaitForBISdk = Boolean(appConfig?.features?.bi_sdk_uri);
  const [recommendationRequestLimit, setRecommendationRequestLimit] = useState(null);
  const [logoOverlayRoomsPromise, setLogoOverlayRoomsPromise] = useState(null);
  const recommendationRequestRef = useRef(null);
  let t5;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = limit => {
      setRecommendationRequestLimit(currentLimit => Math.max(currentLimit ?? 0, limit));
    };
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const requestRecommendations = t5;
  let t6;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      requestRecommendations(CONTROL_BAR_RECOMMENDATIONS_LIMIT);
    };
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const requestControlBarMoreRecommendations = t6;
  let t7;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      requestRecommendations(LOGO_OVERLAY_RECOMMENDATIONS_LIMIT);
    };
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const requestLogoOverlayRecommendations = t7;
  let t8;
  let t9;
  if ($[27] !== isBISdkFailed || $[28] !== isLoadedBISdk || $[29] !== logoOverlayModel || $[30] !== recommendationRequestLimit || $[31] !== shouldWaitForBISdk || $[32] !== worldId) {
    t8 = () => {
      if (recommendationRequestLimit === null) {
        return;
      }
      if ((recommendationRequestRef.current?.limit ?? 0) >= recommendationRequestLimit) {
        return;
      }
      if (!logoOverlayModel || !worldId) {
        return;
      }
      if (shouldWaitForBISdk && !isLoadedBISdk && !isBISdkFailed) {
        return;
      }
      const requestPromise = fetchLogoOverlayRecommendations({
        worldId,
        contentTypeCode: logoOverlayModel.contentTypeCode,
        csdataBid: getClientCookie(RECOMMEND_VISITOR_ID_COOKIE_KEY),
        limit: recommendationRequestLimit
      }).then(_temp).catch(_temp2);
      recommendationRequestRef.current = {
        limit: recommendationRequestLimit,
        promise: requestPromise
      };
      setLogoOverlayRoomsPromise(requestPromise);
    };
    t9 = [logoOverlayModel, worldId, recommendationRequestLimit, shouldWaitForBISdk, isLoadedBISdk, isBISdkFailed];
    $[27] = isBISdkFailed;
    $[28] = isLoadedBISdk;
    $[29] = logoOverlayModel;
    $[30] = recommendationRequestLimit;
    $[31] = shouldWaitForBISdk;
    $[32] = worldId;
    $[33] = t8;
    $[34] = t9;
  } else {
    t8 = $[33];
    t9 = $[34];
  }
  useEffect(t8, t9);
  const showMobilePlayButton = isMobile && isPreferredDevice && !(isVoucherCheckPassed === false && isMobileApp) && !isVideoWorld;
  let t10;
  if ($[35] !== controlBarPresentation || $[36] !== exitRecommendationsPromise || $[37] !== iframeSettings || $[38] !== is3DCommerce || $[39] !== isEmbeddedRequest || $[40] !== isEmbeddedWithoutControlBar || $[41] !== isFullscreen || $[42] !== isVideoWorld || $[43] !== logoOverlayModel || $[44] !== logoOverlayRoomsPromise || $[45] !== showMobilePlayButton || $[46] !== standaloneUrl) {
    t10 = {
      controlBarPresentation,
      standaloneUrl,
      iframeSettings,
      isFullscreen,
      isEmbeddedRequest,
      isEmbeddedWithoutControlBar,
      is3DCommerce,
      isVideoWorld,
      showMobilePlayButton,
      logoOverlayModel,
      logoOverlayRoomsPromise,
      requestControlBarMoreRecommendations,
      requestLogoOverlayRecommendations,
      exitRecommendationsPromise
    };
    $[35] = controlBarPresentation;
    $[36] = exitRecommendationsPromise;
    $[37] = iframeSettings;
    $[38] = is3DCommerce;
    $[39] = isEmbeddedRequest;
    $[40] = isEmbeddedWithoutControlBar;
    $[41] = isFullscreen;
    $[42] = isVideoWorld;
    $[43] = logoOverlayModel;
    $[44] = logoOverlayRoomsPromise;
    $[45] = showMobilePlayButton;
    $[46] = standaloneUrl;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  let t11;
  if ($[48] !== children || $[49] !== t10) {
    t11 = <WorldDetailsContext.Provider value={t10}>{children}</WorldDetailsContext.Provider>;
    $[48] = children;
    $[49] = t10;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  return t11;
}
function _temp2() {
  return [];
}
function _temp(result) {
  return result.ok ? result.rooms : [];
}
export const useWorldDetailsContext = () => {
  const value = useContext(WorldDetailsContext);
  if (value == null) {
    throw new Error("useWorldDetailsContext cannot be used outside of WorldDetailsProvider");
  }
  return value;
};